/**
 * Release feature flags (frontend mirror of papaya/.../featureFlags.ts).
 *
 * Authoritative source at runtime is GET /feature-flags, hydrated into Redux
 * `root.featureFlags` at boot (and re-fetched on workspace switch under
 * multi-org). Before hydration — or for a bundle built before a flag flips —
 * we fall back to the build-time `VITE_FEATURE_*` define. Everything defaults
 * to OFF, so a merged-but-unreleased feature stays dark and a fetch failure
 * hides features rather than exposing them.
 *
 * Three-layer gating: this flag is the master switch (layer 1). RBAC
 * (`useCanViewRisk`, shipping/pipeline permission checks) is layer 2. Server
 * `featureGate` + RBAC middleware is the real boundary (layer 3).
 */
import { useSelector } from 'react-redux'

export const FEATURE_FLAGS = {
  RISK: 'risk',
  SHIPPING: 'shipping',
  PIPELINE: 'pipeline',
} as const

export type FeatureFlagKey = (typeof FEATURE_FLAGS)[keyof typeof FEATURE_FLAGS]

export type FeatureFlagMap = Partial<Record<FeatureFlagKey, boolean>>

const parseEnvFlag = (value: string | undefined): boolean =>
  String(value ?? '').trim().toLowerCase() === 'true'

/**
 * Build-time defaults baked by vite.config.mts `define`. Under Jest the define
 * is not applied, so every value is undefined → false (features dark in tests).
 */
export const buildTimeFeatureFlags = (): Record<FeatureFlagKey, boolean> => ({
  [FEATURE_FLAGS.RISK]: parseEnvFlag(process.env.VITE_FEATURE_RISK_ENABLED),
  [FEATURE_FLAGS.SHIPPING]: parseEnvFlag(process.env.VITE_FEATURE_SHIPPING_ENABLED),
  [FEATURE_FLAGS.PIPELINE]: parseEnvFlag(process.env.VITE_FEATURE_PIPELINE_ENABLED),
})

/**
 * Pure resolver. Prefers the hydrated Redux map; falls back to the build-time
 * default when the flag hasn't been hydrated yet (empty map pre-boot).
 */
export const isFeatureEnabled = (
  key: FeatureFlagKey,
  flags?: FeatureFlagMap | null,
): boolean => {
  if (flags && key in flags) return !!flags[key]
  return buildTimeFeatureFlags()[key]
}

export const selectFeatureFlags = (state: any): FeatureFlagMap =>
  (state?.root?.featureFlags as FeatureFlagMap) || {}

/** Hook for functional components. */
export const useFeatureFlag = (key: FeatureFlagKey): boolean => {
  const flags = useSelector(selectFeatureFlags)
  return isFeatureEnabled(key, flags)
}
